// Verwaltung (nur GQL): Lieferstand je Standort – Fortschritt der Checkliste, Lieferungen, offene Prüfungen.
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { DbPortalLieferstand } from '@gql/shared-types';
import { api, API_BASIS } from '../api/client.js';
import { useAuth } from '../auth/AuthKontext.js';
import { FortschrittBalken, PrioMarke } from '../components/Fortschritt.js';
import { VerwaltungReiter } from '../components/VerwaltungReiter.js';
import { normalisiere } from '../daten/ansicht.js';
import { zeitpunkt } from '../daten/lieferungen.js';

type Filter = 'alle' | 'ohne' | 'pruefen' | 'unvollstaendig' | 'p1';

export function VerwaltungLieferstand() {
  const { zustand } = useAuth();
  const admin = zustand.status === 'bereit' && zustand.ich.nutzer?.rolle === 'gql_admin';
  const [stand, setStand] = useState<DbPortalLieferstand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [suche, setSuche] = useState('');
  const [filter, setFilter] = useState<Filter>('alle');

  useEffect(() => {
    if (!admin) return;
    api<DbPortalLieferstand>('/admin/lieferstand')
      .then(setStand)
      .catch((f: Error) => setFehler(f.message));
  }, [admin]);

  const sichtbar = useMemo(() => {
    if (!stand) return [];
    const q = normalisiere(suche.trim());
    return stand.standorte.filter((s) => {
      if (filter === 'ohne' && s.lieferungen > 0) return false;
      if (filter === 'pruefen' && s.offen === 0) return false;
      if (filter === 'unvollstaendig' && s.fortschritt.vollstaendig) return false;
      if (filter === 'p1' && s.prioritaet !== 1) return false;
      if (q && !normalisiere(`${s.name} ${s.ort ?? ''} ${s.externeId ?? ''} ${s.nutzer.map((n) => n.name).join(' ')}`).includes(q)) return false;
      return true;
    });
  }, [stand, suche, filter]);

  if (!admin) return <Navigate to="/standorte" replace />;
  return (
    <div className="seite">
      <div className="titelzeile">
        <h1>Lieferstand je Standort</h1>
        <a href={`${API_BASIS}/export.xlsx`} className="knopf-sekundaer knopf-auto" download>
          Alles als Excel herunterladen
        </a>
      </div>
      <VerwaltungReiter />
      {fehler && (
        <p className="fehlertext" role="alert">
          {fehler}
        </p>
      )}
      {!stand && !fehler && (
        <p role="status">
          <span className="spinner" aria-hidden="true" /> Lieferstand wird geladen …
        </p>
      )}
      {stand && (
        <>
          <div className="kennzahlen">
            <div className="kennzahl">
              <span className="kennzahl-label">Standorte</span>
              <span className="kennzahl-wert">{stand.summe.standorte}</span>
            </div>
            <div className="kennzahl">
              <span className="kennzahl-label">vollständig</span>
              <span className="kennzahl-wert">{stand.summe.vollstaendig}</span>
            </div>
            <div className="kennzahl">
              <span className="kennzahl-label">ohne Lieferung</span>
              <span className="kennzahl-wert">{stand.summe.ohneLieferung}</span>
            </div>
            <div className="kennzahl">
              <span className="kennzahl-label">zu prüfen</span>
              <span className="kennzahl-wert">{stand.summe.offen}</span>
            </div>
            <div className="kennzahl">
              <span className="kennzahl-label">Unterlagen</span>
              <span className="kennzahl-wert">{stand.summe.dokumente}</span>
            </div>
          </div>
          <div className="filterzeile">
            <label className="feld">
              <span className="feld-label">Suchen</span>
              <input type="search" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Standort, ID, Nutzer" />
            </label>
            <label className="feld">
              <span className="feld-label">Anzeigen</span>
              <select value={filter} onChange={(e) => setFilter(e.target.value as Filter)}>
                <option value="alle">alle</option>
                <option value="pruefen">nur mit Lieferung zur Prüfung</option>
                <option value="ohne">nur ohne Lieferung</option>
                <option value="unvollstaendig">nur unvollständige</option>
                <option value="p1">nur Priorität 1</option>
              </select>
            </label>
          </div>
          {sichtbar.length === 0 ? (
            <p className="leer">Keine Standorte für diese Auswahl.</p>
          ) : (
            <div className="tabelle-rahmen">
              <table className="tabelle tabelle-karten lieferstand">
                <caption className="sr-only">Lieferstand je Standort</caption>
                <thead>
                  <tr>
                    <th scope="col">Standort</th>
                    <th scope="col">Nutzer</th>
                    <th scope="col">Pflichtunterlagen</th>
                    <th scope="col">Lieferungen</th>
                    <th scope="col">Unterlagen</th>
                    <th scope="col">Zuletzt</th>
                  </tr>
                </thead>
                <tbody>
                  {sichtbar.map((s) => (
                    <tr key={s.id}>
                      <td data-label="Standort">
                        <span className="lieferstand-standort">
                          <PrioMarke p={s.prioritaet} />
                          <span>
                            <Link to={`/standorte/${s.id}`}>{s.name}</Link>
                            <span className="zelle-hinweis">
                              {[s.externeId, s.ort, s.gebietName && s.gebietName !== s.ort ? `Gebiet ${s.gebietName}` : null].filter(Boolean).join(' · ') || '–'}
                            </span>
                          </span>
                        </span>
                      </td>
                      <td data-label="Nutzer">{s.nutzer.length ? s.nutzer.map((n) => <div key={n.id}>{n.name}</div>) : <span className="zelle-hinweis">niemand zugeordnet</span>}</td>
                      <td data-label="Pflicht">
                        <FortschrittBalken f={s.fortschritt} />
                      </td>
                      <td data-label="Lieferungen">
                        <span>
                          {s.lieferungen}
                          {s.offen > 0 && <span className="chip chip-l-offen">{s.offen} zu prüfen</span>}
                          {s.nachforderung > 0 && <span className="chip chip-l-nachforderung">{s.nachforderung} Nachforderung</span>}
                        </span>
                      </td>
                      <td data-label="Unterlagen">
                        <span>
                          {s.dokumente}
                          {s.nichtAuswertbar > 0 && <span className="chip chip-fehler">{s.nichtAuswertbar} nicht auswertbar</span>}
                        </span>
                      </td>
                      <td data-label="Zuletzt">{s.letzteLieferung ? zeitpunkt(s.letzteLieferung) : '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
